import React, { useState, useRef } from 'react';

export default function ProjectContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    project_type: 'Ledhejseport / Industriport',
    message: '',
    // Ekstra detaljer (valgfri / fold-ud)
    installation: 'Komplet levering og montering',
    width: '',
    height: '',
    dimension_unit: 'mm',
    company: '',
    address: '',
    timeframe: 'Snarest muligt',
    website_url: '' // Honeypot
  });

  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [fileError, setFileError] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
  const maxTotalSize = 20 * 1024 * 1024; // 20MB
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleFileChange = (e) => {
    setFileError('');
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    if (files.length + selectedFiles.length > 5) {
      setFileError('Du kan højst uploade 5 filer/fotos i alt.');
      return;
    }

    for (const f of selectedFiles) {
      if (!allowedTypes.includes(f.type)) {
        setFileError(`Filtypen "${f.name}" understøttes ikke. Tilladte formater: JPG, PNG, WEBP, GIF, PDF.`);
        return;
      }
      if (f.size > maxFileSize) {
        setFileError(`Filen "${f.name}" er for stor. Maksimum er 10 MB pr. fil.`);
        return;
      }
    }

    const currentTotalSize = files.reduce((acc, f) => acc + f.size, 0);
    const newTotalSize = selectedFiles.reduce((acc, f) => acc + f.size, 0);
    if (currentTotalSize + newTotalSize > maxTotalSize) {
      setFileError('Den samlede filstørrelse må højst være 20 MB.');
      return;
    }

    const newPreviews = selectedFiles.map((file) => {
      if (file.type.startsWith('image/')) {
        return URL.createObjectURL(file);
      }
      return null;
    });

    setFiles(prev => [...prev, ...selectedFiles]);
    setFilePreviews(prev => [...prev, ...newPreviews]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
    setFilePreviews(prev => {
      const urlToRevoke = prev[indexToRemove];
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
      return prev.filter((_, idx) => idx !== indexToRemove);
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Navn skal udfyldes';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes';
    } else if (!/^[+0-9\s-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Indtast et gyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Indtast en gyldig e-mailadresse';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Beskriv venligst dit projekt kort';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      const dataPayload = new FormData();
      dataPayload.append('form_category', 'Nyt Projekt & Tilbud');
      dataPayload.append('project_type', formData.project_type);
      dataPayload.append('name', formData.name);
      dataPayload.append('phone', formData.phone);
      dataPayload.append('email', formData.email);
      dataPayload.append('message', formData.message);

      // Ekstra detaljer
      if (formData.installation) dataPayload.append('installation', formData.installation);
      if (formData.width || formData.height) {
        dataPayload.append('dimensions', `Bredde: ${formData.width || '?'} ${formData.dimension_unit}, Højde: ${formData.height || '?'} ${formData.dimension_unit}`);
      }
      if (formData.company) dataPayload.append('company', formData.company);
      if (formData.address) dataPayload.append('address', formData.address);
      if (formData.timeframe) dataPayload.append('timeframe', formData.timeframe);
      dataPayload.append('website_url', formData.website_url); // Honeypot

      files.forEach((file) => {
        dataPayload.append('attachments', file);
      });

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: dataPayload
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          project_type: 'Ledhejseport / Industriport',
          message: '',
          installation: 'Komplet levering og montering',
          width: '',
          height: '',
          dimension_unit: 'mm',
          company: '',
          address: '',
          timeframe: 'Snarest muligt',
          website_url: ''
        });
        setFiles([]);
        setFilePreviews([]);
        setShowMoreDetails(false);
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Project form submit error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Der skete en netværksfejl. Prøv venligst igen eller ring direkte.');
    }
  };

  // Tæl udfyldte ekstra detaljer
  const countExtraFilled = [
    formData.width.trim() || formData.height.trim() ? 'Mål' : null,
    formData.company.trim(),
    formData.address.trim(),
    formData.installation !== 'Komplet levering og montering' ? formData.installation : null,
    files.length > 0 ? `${files.length} filer` : null
  ].filter(Boolean).length;

  if (status === 'success') {
    return (
      <div className="h-full flex flex-col justify-center rounded-3xl bg-slate-900 border border-slate-800 p-8 sm:p-10 text-white text-center shadow-xl">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 border border-white/20 text-white mb-6 shadow-inner">
          <i className="fa-solid fa-file-signature text-2xl" />
        </div>
        <span className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-2">Projektforespørgsel modtaget</span>
        <h3 className="text-2xl font-extrabold font-display text-white mb-3">Tak for din forespørgsel!</h3>
        <p className="text-sm text-slate-300 leading-relaxed max-w-md mx-auto mb-6">
          Vi gennemgår dine projektoplysninger og vender hurtigt tilbage med et uforpligtende tilbud eller aftale om besigtigelse.
        </p>
        <div className="p-4 rounded-xl bg-slate-800/80 border border-slate-700/60 max-w-sm mx-auto mb-8 text-left text-xs text-slate-300 flex items-start gap-3">
          <i className="fa-solid fa-phone text-white mt-0.5 text-sm shrink-0" />
          <span>Har du spørgsmål til opmåling eller produktvalg? Ring til Christian på <a href="tel:+4521684422" className="text-white font-bold hover:underline">21 68 44 22</a>.</span>
        </div>
        <button
          onClick={() => setStatus('idle')}
          className="mx-auto inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-slate-900 transition-all hover:bg-slate-100 cursor-pointer"
        >
          <i className="fa-regular fa-rotate-left" />
          <span>Opret ny projektforespørgsel</span>
        </button>
      </div>
    );
  }

  return (
    <div className="h-full rounded-3xl bg-white border border-slate-200/90 p-6 sm:p-8 lg:p-10 shadow-xl flex flex-col justify-between relative overflow-hidden">
      {/* Decorative Accent Top Bar */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-800" />

      <div>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-900 text-white border border-slate-800 uppercase tracking-wider mb-2.5">
              <i className="fa-solid fa-ruler-combined text-[11px]" /> Projekt & Nyt Tilbud
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-brand-dark font-display">
              Få tilbud på projekt
            </h3>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
              Få et hurtigt, uforpligtende tilbud på nye porte, hegn eller sikringsanlæg.
            </p>
          </div>
        </div>

        {status === 'error' && (
          <div className="mb-6 rounded-xl bg-rose-50 border border-rose-200 p-4 flex items-start gap-3 text-rose-800 text-sm">
            <i className="fa-regular fa-circle-exclamation text-rose-500 text-lg mt-0.5 shrink-0" />
            <p>{errorMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="project_website_url">Honeypot</label>
            <input
              type="text"
              id="project_website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* BASISOPLYSNINGER */}
          <div className="space-y-4">
            {/* Projekttype */}
            <div>
              <label htmlFor="project_type" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Projekttype <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <select
                  id="project_type"
                  name="project_type"
                  value={formData.project_type}
                  onChange={handleChange}
                  className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50/50 py-2.5 pl-3.5 pr-9 text-sm text-slate-900 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-2 focus:ring-slate-900/15 font-medium cursor-pointer shadow-xs"
                >
                  <option value="Ledhejseport / Industriport">Ledhejseport / Industriport</option>
                  <option value="Hurtigløberport">Hurtigløberport</option>
                  <option value="Skydeport & Fritbærende hegn">Skydeport & Fritbærende hegn</option>
                  <option value="Brandport & Brandgardin">Brandport & Brandgardin</option>
                  <option value="Privat garageport">Privat garageport</option>
                  <option value="Ajax Alarmanlæg & Sikring">Ajax Alarmanlæg & Sikring</option>
                  <option value="Totalløsning / Andet">Totalløsning / Andet</option>
                </select>
                <i className="fa-regular fa-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs" />
              </div>
            </div>

            {/* Kontaktoplysninger i 2 kolonner */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <label htmlFor="project_name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Navn / Kontaktperson <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                    <i className="fa-regular fa-user text-xs" />
                  </span>
                  <input
                    type="text"
                    id="project_name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Fulde navn"
                    className={`w-full rounded-xl border bg-slate-50/50 py-2.5 pl-10 pr-3.5 text-sm text-slate-900 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-2 focus:ring-slate-900/15 shadow-xs ${
                      errors.name ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-200'
                    }`}
                  />
                </div>
                {errors.name && <p className="mt-1 text-xs text-rose-500">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="project_phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Telefonnummer <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                    <i className="fa-regular fa-phone text-xs" />
                  </span>
                  <input
                    type="tel"
                    id="project_phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Tlf. nummer"
                    className={`w-full rounded-xl border bg-slate-50/50 py-2.5 pl-10 pr-3.5 text-sm text-slate-900 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-2 focus:ring-slate-900/15 shadow-xs ${
                      errors.phone ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-200'
                    }`}
                  />
                </div>
                {errors.phone && <p className="mt-1 text-xs text-rose-500">{errors.phone}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="project_email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                E-mailadresse <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                  <i className="fa-regular fa-envelope text-xs" />
                </span>
                <input
                  type="email"
                  id="project_email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="din@email.dk"
                  className={`w-full rounded-xl border bg-slate-50/50 py-2.5 pl-10 pr-3.5 text-sm text-slate-900 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-2 focus:ring-slate-900/15 shadow-xs ${
                    errors.email ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-200'
                  }`}
                />
              </div>
              {errors.email && <p className="mt-1 text-xs text-rose-500">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="project_message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Beskriv dit projekt kort <span className="text-rose-500">*</span>
              </label>
              <textarea
                id="project_message"
                name="message"
                rows="2"
                value={formData.message}
                onChange={handleChange}
                placeholder="F.eks. udskiftning af 2 ledhejseporte på værksted, ny skydeport til indkørsel..."
                className={`w-full rounded-xl border bg-slate-50/50 p-3 text-sm text-slate-900 outline-none transition-all focus:border-slate-900 focus:bg-white focus:ring-2 focus:ring-slate-900/15 shadow-xs ${
                  errors.message ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-200'
                }`}
              />
              {errors.message && <p className="mt-1 text-xs text-rose-500">{errors.message}</p>}
            </div>
          </div>

          {/* FOLD UD KNAP TIL FLERE DETALJER */}
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowMoreDetails(!showMoreDetails)}
              className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 hover:bg-slate-100/90 hover:border-slate-400 text-left transition-all group"
            >
              <div className="flex items-center gap-2.5">
                <span className={`flex h-6 w-6 items-center justify-center rounded-lg text-xs font-bold transition-transform duration-200 ${
                  showMoreDetails ? 'bg-slate-900 text-white rotate-180' : 'bg-slate-200 text-slate-700 group-hover:bg-slate-900 group-hover:text-white'
                }`}>
                  <i className={showMoreDetails ? "fa-solid fa-minus text-[10px]" : "fa-solid fa-plus text-[10px]"} />
                </span>
                <div>
                  <span className="text-xs font-bold text-slate-800 group-hover:text-slate-950 transition-colors block">
                    {showMoreDetails ? 'Skjul ekstra detaljer' : 'Fold ud for at angive flere detaljer'}
                  </span>
                  <span className="text-[11px] text-slate-500 block">
                    Mål (mm/cm), montering, adresse, firmanavn & projektfotos
                  </span>
                </div>
              </div>

              {countExtraFilled > 0 && !showMoreDetails && (
                <span className="bg-slate-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-slate-800 shrink-0">
                  {countExtraFilled} tilføjet
                </span>
              )}
            </button>

            {/* UDFOLDEDE EKSTRA DETALJER */}
            {showMoreDetails && (
              <div className="mt-3 p-4 sm:p-5 rounded-2xl bg-slate-50/90 border border-slate-200 space-y-4 animate-fadeIn">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200/70">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <i className="fa-solid fa-sliders text-slate-900 text-xs" /> Udvidede specifikationer (valgfrit)
                  </span>
                  <span className="text-[11px] text-slate-400">Giver det mest præcise tilbud</span>
                </div>

                {/* MÅL: Bredde x Højde + Enhed */}
                <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <i className="fa-solid fa-ruler text-slate-900 text-xs" /> Omtrentlige åbningsmål
                    </span>
                    <div className="flex items-center gap-2 text-xs">
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="dimension_unit"
                          value="mm"
                          checked={formData.dimension_unit === 'mm'}
                          onChange={handleChange}
                          className="accent-slate-900 text-slate-900 focus:ring-slate-900"
                        />
                        <span className="font-semibold text-slate-700 text-xs">mm</span>
                      </label>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="dimension_unit"
                          value="cm"
                          checked={formData.dimension_unit === 'cm'}
                          onChange={handleChange}
                          className="accent-slate-900 text-slate-900 focus:ring-slate-900"
                        />
                        <span className="font-semibold text-slate-700 text-xs">cm</span>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="project_width" className="block text-[11px] font-semibold text-slate-600 mb-1">
                        Bredde ({formData.dimension_unit})
                      </label>
                      <input
                        type="text"
                        id="project_width"
                        name="width"
                        value={formData.width}
                        onChange={handleChange}
                        placeholder={formData.dimension_unit === 'mm' ? 'F.eks. 3500' : 'F.eks. 350'}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-1.5 px-3 text-xs text-slate-900 outline-none transition-all focus:border-slate-900 focus:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="project_height" className="block text-[11px] font-semibold text-slate-600 mb-1">
                        Højde ({formData.dimension_unit})
                      </label>
                      <input
                        type="text"
                        id="project_height"
                        name="height"
                        value={formData.height}
                        onChange={handleChange}
                        placeholder={formData.dimension_unit === 'mm' ? 'F.eks. 3000' : 'F.eks. 300'}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-1.5 px-3 text-xs text-slate-900 outline-none transition-all focus:border-slate-900 focus:bg-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label htmlFor="installation" className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Montering / Omfang
                    </label>
                    <div className="relative">
                      <select
                        id="installation"
                        name="installation"
                        value={formData.installation}
                        onChange={handleChange}
                        className="w-full appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-8 text-xs text-slate-900 outline-none transition-all focus:border-slate-900 font-medium shadow-xs"
                      >
                        <option value="Komplet levering og montering">Komplet levering og montering</option>
                        <option value="Kun levering af port/materiel">Kun levering af port/materiel</option>
                        <option value="Udskiftning af eksisterende port">Udskiftning af eksisterende port</option>
                        <option value="Ønsker rådgivning & opmåling først">Ønsker rådgivning & opmåling først</option>
                      </select>
                      <i className="fa-regular fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[10px]" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="project_company" className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Virksomhed / Forening
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                        <i className="fa-regular fa-building text-xs" />
                      </span>
                      <input
                        type="text"
                        id="project_company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Firmanavn"
                        className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs text-slate-900 outline-none transition-all focus:border-slate-900 shadow-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label htmlFor="project_address" className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Projektadresse / Postnr. & By
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                        <i className="fa-regular fa-location-dot text-xs" />
                      </span>
                      <input
                        type="text"
                        id="project_address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="F.eks. Byvej 4, 6000 Kolding"
                        className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs text-slate-900 outline-none transition-all focus:border-slate-900 shadow-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="project_timeframe" className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Ønsket tidsramme
                    </label>
                    <div className="relative">
                      <select
                        id="project_timeframe"
                        name="timeframe"
                        value={formData.timeframe}
                        onChange={handleChange}
                        className="w-full appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-8 text-xs text-slate-900 outline-none transition-all focus:border-slate-900 font-medium shadow-xs"
                      >
                        <option value="Snarest muligt">Snarest muligt</option>
                        <option value="Inden for 1-2 måneder">Inden for 1-2 måneder</option>
                        <option value="3-6 måneder frem (nybyggeri/renovering)">3-6 måneder frem</option>
                        <option value="Kun uforpligtende budgetpris">Kun uforpligtende budgetpris</option>
                      </select>
                      <i className="fa-regular fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[10px]" />
                    </div>
                  </div>
                </div>

                {/* Fotos & Tegninger */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Vedhæft fotos af åbning, facade eller skitse
                  </label>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={handleFileChange}
                      className="hidden"
                      id="project_file_upload"
                    />
                    <label
                      htmlFor="project_file_upload"
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-dashed border-slate-300 bg-white hover:bg-slate-100 hover:border-slate-400 text-slate-600 text-xs font-medium cursor-pointer transition-colors shadow-xs"
                    >
                      <i className="fa-solid fa-cloud-arrow-up text-slate-900 text-sm" />
                      <span>Upload fotos af facade, åbning eller målskitse</span>
                    </label>

                    {fileError && <p className="text-xs text-rose-500">{fileError}</p>}

                    {files.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {files.map((file, idx) => (
                          <div
                            key={idx}
                            className="relative flex items-center gap-2 p-1.5 px-2.5 rounded-xl bg-white border border-slate-200 overflow-hidden shadow-xs"
                          >
                            {filePreviews[idx] ? (
                              <img
                                src={filePreviews[idx]}
                                alt={file.name}
                                className="h-6 w-6 rounded-md object-cover shrink-0 border border-slate-200"
                              />
                            ) : (
                              <div className="h-6 w-6 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
                                <i className="fa-solid fa-file-pdf text-[10px]" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold text-slate-800 max-w-[110px] truncate">{file.name}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              className="text-slate-400 hover:text-rose-500 transition-colors ml-1 p-0.5"
                              title="Fjern fil"
                            >
                              <i className="fa-solid fa-xmark text-[10px]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-slate-900/25 transition-all hover:bg-black active:scale-[0.99] disabled:opacity-70 cursor-pointer"
            >
              {status === 'submitting' ? (
                <>
                  <i className="fa-solid fa-spinner-third animate-spin" />
                  <span>Beregner & sender...</span>
                </>
              ) : (
                <>
                  <span>Indhent uforpligtende tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </>
              )}
            </button>
          </div>

          <div className="flex items-center justify-center gap-4 text-xs text-slate-500 pt-1">
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-solid fa-check text-slate-900 text-sm font-bold" />
              100% uforpligtende tilbud
            </span>
            <span>•</span>
            <span className="inline-flex items-center gap-1.5">
              <i className="fa-solid fa-shield-check text-slate-900 text-xs" />
              Rådgivning & opmåling
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
